import { useSearchParams } from 'react-router-dom';
import { Tabs } from '../../job-roles/components/Tabs';
import { ChartOfAccountsTab } from './ChartOfAccountsTab';
import { CodeMappingsTab } from './CodeMappingsTab';
import { ExportHistoryTab } from './ExportHistoryTab';
import { ExportSettingsTab } from './ExportSettingsTab';
import { GlExportsPage } from './GlExportsPage';
import { GlMappingTab } from './GlMappingTab';
import { GlMappingsPage } from './GlMappingsPage';
import { PayrollExportTab } from './PayrollExportTab';
import '../../job-roles/job-roles.css';
import '../accounting-export.css';

const TABS = [
  { key: 'payroll-export', label: 'Payroll Export' },
  { key: 'gl-mapping', label: 'GL Mapping' },
  { key: 'chart-of-accounts', label: 'Chart of Accounts' },
  { key: 'history', label: 'History' },
  { key: 'export-settings', label: 'Export settings' },
  { key: 'code-mappings', label: 'Code mappings' },
  { key: 'legacy-mappings', label: 'Legacy GL Mappings (8 categories)' },
  { key: 'legacy-exports', label: 'Legacy GL Exports & Reconciliation' },
] as const;

type TabKey = (typeof TABS)[number]['key'];
const DEFAULT_TAB: TabKey = 'payroll-export';
const isTab = (value: string | null): value is TabKey => TABS.some((t) => t.key === value);

function LegacyNote() {
  return (
    <p className="jr-notice ax-legacy-note" role="note">
      This is the <strong>legacy 8-category GL voucher</strong> screen. It maps eight broad categories and allows one voucher per payroll run.
      To export a finalized payroll as a journal-entry CSV with detailed GL mappings, use the Payroll Export tab.
    </p>
  );
}

/**
 * Accounting Export. The default tab, Payroll Export, turns a finalized payroll into a balanced journal entry and a CSV file; GL Mapping, Chart of
 * Accounts and History support it, Export settings holds the company defaults for the journal options and Code mappings records deduction, earning and benefit code mappings. The two original tabs (the legacy 8-category GL voucher and its reconciliation) are kept, unchanged, and labelled as legacy.
 * The tab and the selected payroll or export live in the URL (`?tab=&runId=&exportId=`) so a link, a reload and the Back button all land where they were.
 */
export function AccountingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab');
  const tab: TabKey = isTab(requested) ? requested : DEFAULT_TAB;

  return (
    <div className="ax-page jr-page">
      <h2>Accounting Export</h2>
      <p className="muted ax-lead">
        Export finalized payroll as a balanced journal entry in a CSV file. CSV only in this release: direct connections to accounting software are not available yet.
      </p>
      <Tabs
        tabs={TABS.map((t) => ({ key: t.key, label: t.label }))}
        active={tab}
        onChange={(key) => setSearchParams({ tab: key })}
        idPrefix="acx"
        label="Accounting export sections"
      >
        {tab === 'payroll-export' && <PayrollExportTab />}
        {tab === 'gl-mapping' && <GlMappingTab />}
        {tab === 'chart-of-accounts' && <ChartOfAccountsTab />}
        {tab === 'history' && <ExportHistoryTab />}
        {tab === 'export-settings' && <ExportSettingsTab />}
        {tab === 'code-mappings' && <CodeMappingsTab />}
        {tab === 'legacy-mappings' && <><LegacyNote /><GlMappingsPage /></>}
        {tab === 'legacy-exports' && <><LegacyNote /><GlExportsPage /></>}
      </Tabs>
    </div>
  );
}
